import React from "react";
import { Building2, Recycle, MapPin, SlidersHorizontal, Coins, Target, Boxes, Layers, ChevronDown } from "lucide-react";
import RingsOrbit from "./RingsOrbit";
import LocationSearch from "./LocationSearch";
import LocationMap from "./LocationMap";
import WeatherPanel from "./WeatherPanel";
import BuildingConfigPanel from "./BuildingConfigPanel";
import FinancialsPanel from "./FinancialsPanel";
import { colors, fonts } from "../theme";
import { useStudio } from "../store/useStudio";
import { PILLAR_BY_ID } from "../data/matrices";
import { relationalTags } from "../lib/kbLogic";
import { computeRipple } from "../lib/anchorRipple";
import { styles, RING_COLOR } from "./dashboard/tokens";
import { ReferencesButton } from "./dashboard/primitives";
import { InfoTip } from "./InfoTip";
import { SystemBlocksPanel } from "./dashboard/blocks";
import { RippleBanner } from "./dashboard/drafting";
import { CircularityGaps, RingGuideCard, AdvancedKB, ScenarioFacePanel, PillarOptions, FlowStepper } from "./dashboard/panels";

export default function Dashboard({ applyLocation, onEpwFile, onEpwClear, viability, kpis }) {
  const {
    kb, selections, rootPillar, setRoot, toggle,
    areas, setArea, areaFocusSystem, setAreaFocus,
    resourceBlocks, setResourceBlock, systemBlocks, activeBlockId, setActiveBlock, removeSystemBlock,
    setBlockAllocation, addBlockCrop, removeBlockCrop, resetBlockShares,
    scenarioFaces, clearRing, requestAutoPlace, activeFaceId,
    allocationMode, model,
    locationAreas, setLocationArea, requestAutoPlaceLocation,
    locationBlocks, activeLocationBlockId, setActiveLocationBlock, removeLocationBlock,
    geo, weather, weatherLoading,
    pickTool, setPickTool, requestBlockResize,
    pairDraft, beginPair, setPairField, clearPairDraft, commitPair,
    pausedDrafts, pauseDraft, resumeDraft, removePausedDraft, flashPausedDrafts,
    anchorRing, opportunity, occupancy, grossAreas,
    allocDraw, setAllocDraw,
    cropDraft, beginCropDraft, setCropDraftSystem, clearCropDraft, commitCropDraft,
  } = useStudio();

  // Auto-scroll UX: bring a freshly placed / selected instance or captured surface into view.
  React.useEffect(() => {
    const id = activeBlockId || activeLocationBlockId || activeFaceId;
    if (!id) return;
    const el = document.querySelector(`[data-block-scroll="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeBlockId, activeLocationBlockId, activeFaceId]);

  const progress = viability?.scores || { locations: 0, systems: 0, crops: 0, resources: 0 };
  const counts = {
    locations: selections.locations.length,
    systems: selections.systems.length,
    crops: selections.crops.length,
    resources: selections.resources.length,
  };
  const pillar = PILLAR_BY_ID[rootPillar];
  // Phase 3 — Relational Mesh: bidirectional cross-ring compatibility tags, computed globally.
  // These Recommended/dim hints only surface while ACTIVELY drafting an option (an anchor or open
  // draft exists). On "Done" the anchor is purged, so the option list returns to a fully-lit, neutral
  // state — no held-over faded/recommended bias bleeds into the next drafted option.
  const drafting = !!anchorRing || !!pairDraft || !!cropDraft;
  const relTagsRaw = React.useMemo(() => relationalTags(kb, selections), [kb, selections]);
  const relTags = drafting ? relTagsRaw : { locations: {}, systems: {}, crops: {}, resources: {} };
  // Hotfix 5.0 — Anchor & Ripple: derive supply/demand bridge from the anchor ring.
  const ripple = React.useMemo(
    () => computeRipple({ anchorRing, opportunity, occupancy, systemBlocks, pairDraft, kpis, grossAreas, selections }),
    [anchorRing, opportunity, occupancy, systemBlocks, pairDraft, kpis, grossAreas, selections]
  );
  // Hotfix 5.0 — 3-tier funnel ring fills (nested proportions, all vs the Viable/opportunity area).
  // Allocation ring (Locations) = Allocated ÷ Viable; Footprint ring (Systems) = Footprint ÷ Viable.
  // Because Footprint ⊆ Allocated ⊆ Viable, the rings nest (inner ≤ outer) instead of the Footprint
  // ring snapping to 100%. The Footprint-vs-Allocated relationship surfaces as the "% packed" / warn.
  // Rings CLAMP at 100% visually; rawRingProgress below preserves the true (possibly >100%) math.
  const ringProgress = React.useMemo(() => {
    const va = ripple.viableArea, aa = ripple.allocatedArea, sf = ripple.systemFootprint;
    return {
      locations: va > 0 ? Math.min(1, aa / va) : (progress.locations || 0),
      systems: va > 0 ? Math.min(1, sf / va) : (progress.systems || 0),
      crops: ripple.yieldTarget > 0 ? Math.min(1, ripple.yieldRealized / ripple.yieldTarget) : (progress.crops || 0),
      resources: progress.resources || 0,
    };
  }, [ripple, progress]);
  const rawRingProgress = React.useMemo(() => ({
    locations: ripple.viableArea > 0 ? ripple.allocatedArea / ripple.viableArea : 0,
    systems: ripple.viableArea > 0 ? ripple.systemFootprint / ripple.viableArea : 0,
    crops: ripple.yieldTarget > 0 ? ripple.yieldRealized / ripple.yieldTarget : 0,
    resources: progress.resources || 0,
  }), [ripple, progress]);

  return (
    <aside style={styles.panel} data-testid="dashboard">
      <header style={styles.header}>
        <div>
          <div style={styles.brand}>Resource Circularity Engine</div>
          <div style={styles.brandSub}>{geo?.name || "Building-Integrated Agriculture"}</div>
        </div>
      </header>

      <div style={styles.scroll}>
        <div style={styles.scopeDisclaimer} data-testid="bia-scope-disclaimer">
          <span style={styles.scopeTitle}>Resource Circularity Engine</span>
          Resource Circularity Engine is a specialized integration engine for Building Integrated
          Agriculture. It strictly tracks and optimizes the recovery and valorization of building waste streams
          (water, heat, organics) and site renewables to sustain onsite food production. All metrics reflect
          this symbiotic loop.
        </div>
        <CollapsibleGroup title="Site & Climate" subtitle={geo?.name || ""} icon={MapPin} accent={RING_COLOR.locations} defaultOpen={true} testId="group-site-climate">
          <LocationMap
            value={geo}
            onPin={(p) => applyLocation({ ...p })}
            epwStation={weather?.source === "epw" && weather.stationLat != null ? { lat: weather.stationLat, lng: weather.stationLng, name: weather.stationName } : null}
          />
          <div style={{ height: 10 }} />
          <LocationSearch onSelect={applyLocation} />
          <WeatherPanel weather={weather} loading={weatherLoading} onEpwFile={onEpwFile} onEpwClear={onEpwClear} geo={geo} />
        </CollapsibleGroup>

        {model?.status === "ready" && (
          <CollapsibleGroup title="Building Configuration" info={{ body: "The building's model dimensions and envelope: footprint, height and surface areas that drive the area calculations." }} icon={Building2} accent={RING_COLOR.systems} nudge testId="group-building-config">
            <BuildingConfigPanel annualYieldKg={kpis?.total_yield || 0} />
          </CollapsibleGroup>
        )}

        <CollapsibleGroup title="Circularity Loops" subtitle="" icon={Target} accent={RING_COLOR.crops} static testId="group-rings">
          {anchorRing && <RippleBanner ripple={ripple} opportunity={opportunity} onClearAnchor={() => setRoot(anchorRing)} />}
          <RingsOrbit progress={ringProgress} rawProgress={rawRingProgress} funnel={{ viableArea: ripple.viableArea, allocatedArea: ripple.allocatedArea, systemFootprint: ripple.systemFootprint }} counts={counts} rootPillar={rootPillar} onSelectRoot={setRoot} kpis={kpis} constraint={ripple.constraint} />
        </CollapsibleGroup>

        {model?.status === "ready" && kpis?.balance && (systemBlocks?.length > 0 || (kpis?.balance?.locked_count || 0) > 0) && (
          <CollapsibleGroup title="Circularity Gaps" info={{ body: "Unclosed resource loops in the design; these drive the Resources ring." }} icon={Recycle} accent={RING_COLOR.resources} nudge testId="group-circularity-gaps">
            <CircularityGaps kpis={kpis} />
          </CollapsibleGroup>
        )}

        {model?.status === "ready" && kb && <FlowStepper active={rootPillar} />}

        <CollapsibleGroup title={`Configure ${pillar.label}`} info={(rootPillar === "locations" || rootPillar === "systems") ? { title: "Zone + system pairing", body: "Each instance places a required system within an optional allocation zone. If no zone is set, the system's own footprint defines it." } : undefined} icon={SlidersHorizontal} accent={pillar.solid} badge={(rootPillar === "locations" || rootPillar === "systems") ? { count: pausedDrafts.length, onClick: flashPausedDrafts } : undefined} nudge={model?.status === "ready" && !!kb} testId="group-configure">
          <RingGuideCard pillar={rootPillar} accent={pillar.solid} ceiling={viability?.ceilings?.[rootPillar]} />
          {kb ? (
            <PillarOptions
              kb={kb}
              rootPillar={rootPillar}
              allocationMode={allocationMode[rootPillar]}
              relTags={relTags}
              pairDraft={pairDraft}
              beginPair={beginPair}
              setPairField={setPairField}
              clearPairDraft={clearPairDraft}
              commitPair={commitPair}
              pausedDrafts={pausedDrafts}
              pauseDraft={pauseDraft}
              resumeDraft={resumeDraft}
              removePausedDraft={removePausedDraft}
              selections={selections}
              toggle={toggle}
              weather={weather}
              areas={areas}
              setArea={setArea}
              areaFocusSystem={areaFocusSystem}
              setAreaFocus={setAreaFocus}
              allocDraw={allocDraw}
              setAllocDraw={setAllocDraw}
              grossAreas={grossAreas}
              cropDraft={cropDraft}
              beginCropDraft={beginCropDraft}
              setCropDraftSystem={setCropDraftSystem}
              clearCropDraft={clearCropDraft}
              commitCropDraft={commitCropDraft}
              pickTool={pickTool}
              setPickTool={setPickTool}
              resourceBlocks={resourceBlocks}
              setResourceBlock={setResourceBlock}
              modelReady={model?.status === "ready"}
              requestAutoPlace={requestAutoPlace}
              ripple={ripple}
              locationAreas={locationAreas}
              setLocationArea={setLocationArea}
              requestAutoPlaceLocation={requestAutoPlaceLocation}
              locationBlocks={locationBlocks}
              activeLocationBlockId={activeLocationBlockId}
              setActiveLocationBlock={setActiveLocationBlock}
              removeLocationBlock={removeLocationBlock}
              systemBlocks={systemBlocks}
              activeBlockId={activeBlockId}
              setActiveBlock={setActiveBlock}
              removeSystemBlock={removeSystemBlock}
              addBlockCrop={addBlockCrop}
              removeBlockCrop={removeBlockCrop}
              setBlockAllocation={setBlockAllocation}
              resetBlockShares={resetBlockShares}
            />
          ) : (
            <div style={styles.hint}>Loading knowledge base…</div>
          )}
          {/* Systems ring only: the sub-area footprint drafting / placement card, kept contextual. */}
          {rootPillar === "systems" && <ScenarioFacePanel kb={kb} />}
          <CollapsibleGroup title="Financial Model" info={{ body: "Capital cost (CAPEX), operating cost (OPEX) and payback assumptions for the design." }} icon={Coins} accent={RING_COLOR.systems} testId="group-financials">
            <FinancialsPanel />
            <div style={{ marginTop: 10 }}>
              <ReferencesButton citations={kb?.citations} title="References & Data Sources" label="View all references" testId="references-button-global" />
            </div>
          </CollapsibleGroup>
          {(() => {
            const ringFaces = scenarioFaces.filter((f) => f.pillar === rootPillar).length;
            const ringBlocks = rootPillar === "systems" ? systemBlocks.length : 0;
            const n = counts[rootPillar] + ringFaces + ringBlocks;
            if (!n) return null;
            return (
              <button
                style={styles.clearRingBtn}
                data-testid={`clear-ring-${rootPillar}`}
                onClick={() => { clearRing(rootPillar); setActiveBlock(null); }}
              >
                Clear {pillar.label} Selections ({n})
              </button>
            );
          })()}
        </CollapsibleGroup>

        {systemBlocks.filter((b) => !selections.systems.includes(b.systemId)).length > 0 && (
          <CollapsibleGroup title="Other Drafted Systems" info={{ body: "Drafted system blocks that aren't part of the currently active loop." }} icon={Boxes} accent={RING_COLOR.systems} nudge testId="group-drafted-systems">
            <SystemBlocksPanel
              kb={kb}
              blocks={systemBlocks.filter((b) => !selections.systems.includes(b.systemId))}
              activeBlockId={activeBlockId}
              onSelect={setActiveBlock}
              onRemove={removeSystemBlock}
              onAddCrop={addBlockCrop}
              onRemoveCrop={removeBlockCrop}
              onShare={setBlockAllocation}
              onReset={resetBlockShares}
            />
          </CollapsibleGroup>
        )}

        <CollapsibleGroup title="Advanced · Knowledge Base" info={{ body: "Override the baseline knowledge-base values the engine uses." }} icon={Layers} accent={colors.textTertiary} testId="group-advanced-kb">
          <AdvancedKB kb={kb} />
        </CollapsibleGroup>

        <div style={styles.copyright} data-testid="app-copyright">© 2026 Resource Circularity Engine. All Rights Reserved.</div>
      </div>
    </aside>
  );
}

// Progressive-disclosure wrapper — every right-rail zone lives in a clearly
// bordered card. Secondary zones start collapsed; once an upstream zone holds
// real content the next collapsed card's chevron softly glows to invite the
// user to keep digging. `static` renders a non-collapsible bordered group.
function CollapsibleGroup({ title, subtitle, info, icon: Icon, accent = colors.brand, defaultOpen = false, nudge = false, static: isStatic = false, badge, testId, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  // Quick Tour finish (and any global collapse) folds every non-static group shut.
  const collapseSignal = useStudio((s) => s.sidebarCollapseSignal);
  React.useEffect(() => { if (!isStatic && collapseSignal > 0) setOpen(false); }, [collapseSignal, isStatic]);
  const showNudge = nudge && !open && !isStatic;
  const HeaderTag = isStatic ? "div" : "button";
  return (
    <section style={styles.group} data-testid={testId} data-open={open}>
      <HeaderTag
        type={isStatic ? undefined : "button"}
        style={{ ...styles.groupHeader, cursor: isStatic ? "default" : "pointer" }}
        onClick={isStatic ? undefined : () => setOpen((v) => !v)}
        data-testid={testId ? `${testId}-${isStatic ? "header" : "toggle"}` : undefined}
        aria-expanded={isStatic ? undefined : open}
      >
        {Icon && (
          <span style={{ ...styles.groupIcon, background: `${accent}18`, color: accent }}>
            <Icon size={15} />
          </span>
        )}
        <span style={styles.groupTitleWrap}>
          <span style={{ ...styles.groupTitle, display: "inline-flex", alignItems: "center", gap: 5 }}>
            {title}
            {info && <InfoTip testId={testId ? `${testId}-info` : undefined} title={info.title || title} body={info.body} placement="bottom" />}
          </span>
          {subtitle && <span style={styles.groupSub}>{subtitle}</span>}
        </span>
        {badge?.count > 0 && (
          <span
            role="button"
            title={`${badge.count} paused draft${badge.count === 1 ? "" : "s"}. View the list`}
            onClick={badge.onClick ? (e) => { e.stopPropagation(); if (!open && !isStatic) setOpen(true); badge.onClick(); } : undefined}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 6, padding: "2px 8px", borderRadius: 999, background: `${accent}1a`, color: accent, fontFamily: fonts.mono, fontSize: 10, fontWeight: 800, letterSpacing: 0.3, whiteSpace: "nowrap", cursor: badge.onClick ? "pointer" : "default" }}
            data-testid={testId ? `${testId}-badge` : "group-badge"}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: accent }} />
            {badge.count} paused
          </span>
        )}
        {!isStatic && (
          <span
            className={showNudge ? "bia-nudge-chevron" : undefined}
            style={{ ...styles.groupChevron, transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
            data-testid={testId ? `${testId}-chevron` : undefined}
            data-nudge={showNudge ? "true" : "false"}
          >
            <ChevronDown size={16} />
          </span>
        )}
      </HeaderTag>
      {(open || isStatic) && (
        <div style={styles.groupBody} className={isStatic ? undefined : "bia-group-body"}>
          {children}
        </div>
      )}
    </section>
  );
}
